import formatUTCDate from '@/shared/tools/formatUTCDate'
import type { ContactSubscriptionData } from './contactSubscriptionData'

// || '' is used to prevent sending 'null' as a string value

type ContactFormParametersGenerationParams = {
  contactData: ContactSubscriptionData
  id: string | number
  country: string
  action: string
  doorToDoorPartnerChecklistCheckedTitles: Record<string, boolean>
  doorToDoorPartnerLeadSource: string
}

export const contactFormParametersGeneration = ({
  contactData,
  id,
  country,
  action,
  doorToDoorPartnerChecklistCheckedTitles,
  doorToDoorPartnerLeadSource,
}: ContactFormParametersGenerationParams): URLSearchParams => {
  const options = new URLSearchParams()

  if (action !== '') {
    options.append('action_requested', action)
  }

  const {
    personal: {
      zohoId,
      civility = '-1',
      lastName = '',
      firstName = '',
      email = '',
      phone1 = '',
      phone2 = '',
      secondName,
      civility2 = '-1',
      lastName2 = '',
      firstName2 = '',
      birthdate = '',
      mcpAdvisoryId = null,
    },
    preferences: {
      dataCollectionAuth,
      neverBounceResult,
      callbackTime,
      callbackDate,
      clientType,
      whatsappConsent,
      createEkomiInteraction,
    },
    professional: { socialReason, siret, activity, apeCode, taxId },
    additional: { comments = '', referral, referralFirstName = '', referralLastName = '' } = {},
  } = contactData

  options.append('id', String(id))
  options.append('zoho_id', zohoId ?? '')
  options.append('lang', country)

  // Preferences
  options.append('disclaimer', dataCollectionAuth ? '1' : '0')
  options.append('neverbounce_result', String(neverBounceResult ?? ''))
  options.append('whatsapp_consent', whatsappConsent ? '1' : '0')
  if (createEkomiInteraction) {
    options.append('create_ekomi_interaction', '1')
  }

  if (callbackTime && callbackDate && callbackTime !== '' && callbackDate !== '') {
    const date = new Date(`${callbackDate} ${callbackTime}`).toISOString().slice(0, 19)
    options.append('web_callback', formatUTCDate(date) || '')
  }

  // Professional
  options.append('pro_social_reason', socialReason || '')
  options.append('pro_siret', siret || '')
  options.append('pro_activity', activity || '')
  options.append('pro_ape_code', apeCode || '')
  options.append('is_professional', clientType === 'pro' ? '1' : '0')

  // to be added in dataaase in the future
  options.append('pro_tax_id', taxId || '')

  // Personal data
  options.append('civility', civility || '')
  options.append('lastname', lastName || '')
  options.append('firstname', firstName || '')
  options.append('email', email || '')
  if (birthdate) options.append('birthday', birthdate || '')
  options.append('phone1', phone1 || '')
  options.append('phone2', phone2 || '')
  options.append('secondName', secondName === true ? '1' : '0')
  if (secondName) {
    options.append('civility2', civility2 || '')
    options.append('lastname2', lastName2 || '')
    options.append('firstname2', firstName2 || '')
  }

  // MCP advisor assignment (Selectra admins / FR only). Only append when
  // explicitly set so we don't accidentally clear it on contacts that have
  // never had one.
  if (mcpAdvisoryId !== null && mcpAdvisoryId !== undefined && String(mcpAdvisoryId).length > 0) {
    options.append('mcp_advisory_id', String(mcpAdvisoryId))
  }

  // Additional data
  if (comments) {
    options.append('subscription_comments', comments)
  }
  options.append('referral', referral === true ? '1' : '0')
  if (referral) {
    options.append('referral_firstname', referralFirstName || '')
    options.append('referral_lastname', referralLastName || '')
  }

  // Door-to-door partner checklist
  const allChecked = Object.values(doorToDoorPartnerChecklistCheckedTitles).every(Boolean)
  if (allChecked && doorToDoorPartnerLeadSource) {
    options.append('log_door_to_door_partner_completed_checklist', doorToDoorPartnerLeadSource)
  }

  return options
}
